import { ArrowLeft, Settings } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { NavLink, useParams } from 'react-router';
import {
  useGetProductLengthsByProductId,
  useUpsertSellerInventoryAndProductConfiguration,
  useUpsertSellerProductConfiguration,
} from '~/api/my-products';
import { ProductSettingModal } from '~/components/cards/ProductSettingModal';
import { Modal } from '~/components/overlays';
import { Button, Form, useFormData } from '~/components/ui';
import type {
  SellerProductInventory,
  SellerProductStatus,
  UnitCode,
  UpsertSellerInventoryAndProductConfigurationMutationVariables,
} from '~/graphql/graphql';
import type { FormField } from '~/types';
import { extractGraphQLErrorMessages, formatCurrency } from '~/utils';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

export function EditMyProduct() {
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const { t } = useTranslation('my-product');

  const inventoryId = useParams()?.inventoryId || '';
  const { data } = useGetProductLengthsByProductId(inventoryId);
  const sellerProductInventory = data?.sellerProductInventory;
  const inventoryUnitCode = sellerProductInventory?.sellerProductConfiguration
    .unit as UnitCode;

  const initialData = getInitialFormData(
    sellerProductInventory as SellerProductInventory,
    inventoryUnitCode
  );
  const formData = useFormData({ initialData });
  const formattedPriceWithUnit = formatPriceWithUnit(
    sellerProductInventory?.platformProductsLength.latestPriceMid
  );

  const { mutate: upsertSellerProduct, isPending: isUpsertPending } =
    useUpsertSellerInventoryAndProductConfiguration();

  const {
    mutate: upsertSellerProductConfiguration,
    isPending: isUpsertConfigurationPending,
  } = useUpsertSellerProductConfiguration();

  const handleSaveChanges = () => {
    const payload: UpsertSellerInventoryAndProductConfigurationMutationVariables =
      {
        platformProductsLengthId: sellerProductInventory?.platformProductsLength
          .id as string,
        quantity: {
          value: parseFloat(formData?.data.stock as string),
          unit: formData?.data.unit as UnitCode,
        },
        sellerProductConfigurationInput: {
          pricePerUnit: parseFloat(formData?.data.pricePerUnit as string),
          minimumOrderQuantity: {
            value: parseFloat(formData?.data.minimumOrderQuantity as string),
            unit: formData?.data.unit as UnitCode,
          },
          readyToShipDays: parseInt(formData?.data.readyToShipDays as string),
          replenishmentRatePerWeek: {
            value: parseInt(formData?.data.replenishmentRatePerWeek as string),
            unit: formData?.data.unit as UnitCode,
          },
          internalNotes: formData?.data.internalNotes as string,
          status: formData?.data.status as SellerProductStatus,
        },
      };
    upsertSellerProduct(payload, {
      onSuccess: () => {
        toast.success(t('messages.product_inventory_updated_successfully'));
      },
      onError: (error) =>
        toast.error(extractGraphQLErrorMessages(error).join(', ')),
    });
  };

  const handleSaveSettings = (
    criticalStockLevel: number,
    lowStockLevel: number
  ) => {
    const currentReplenishment =
      sellerProductInventory?.sellerProductConfiguration
        .replenishmentRatePerWeek;
    const settingsUnit = inventoryUnitCode ?? ('MBF' as UnitCode);
    const payload = {
      platformProductLengthId:
        sellerProductInventory?.platformProductsLength.id || '',
      sellerProductConfigurationInput: {
        criticalStockLevel: { value: criticalStockLevel, unit: settingsUnit },
        lowStockLevel: { value: lowStockLevel, unit: settingsUnit },
        replenishmentRatePerWeek: {
          value: currentReplenishment?.value ?? 0,
          unit: currentReplenishment?.unit || settingsUnit,
        },
      },
    };
    upsertSellerProductConfiguration(payload, {
      onSuccess: () => {
        toast.success(t('messages.product_updated_successfully'));
        setShowSettingsModal(false);
      },
      onError: (error) =>
        toast.error(extractGraphQLErrorMessages(error).join(', ')),
    });
  };

  return (
    <>
      <Modal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      >
        <ProductSettingModal
          initialCriticalStockLevel={
            sellerProductInventory?.sellerProductConfiguration
              .criticalStockLevel.value || 0
          }
          initialLowStockLevel={
            sellerProductInventory?.sellerProductConfiguration.lowStockLevel
              .value || 0
          }
          stockQuantity={sellerProductInventory?.quantity?.value || 0}
          replenishmentRate={
            sellerProductInventory?.sellerProductConfiguration
              ?.replenishmentRatePerWeek?.value || 0
          }
          onClose={() => setShowSettingsModal(false)}
          onSave={handleSaveSettings}
          isSaving={isUpsertConfigurationPending}
        />
      </Modal>
      <div className="bg-neut-0 min-h-screen">
        <div className="bg-neut-0 border-b border-neut-200">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <NavLink
                to="/my-products"
                className="p-2 hover:bg-neut-100 rounded-lg transition-colors"
                data-testid="back-to-my-products"
              >
                <ArrowLeft size={20} />
              </NavLink>
              <h1 className="text-xl text-neut-prim font-semibold">
                {t('edit_product.title')}
              </h1>
            </div>
            <Button
              onClick={handleSaveChanges}
              disabled={isUpsertPending}
              width="12rem"
            >
              {isUpsertPending
                ? t('edit_product.saving')
                : t('edit_product.save_changes')}
            </Button>
          </div>
        </div>
        <div className="space-y-8 px-6 py-6">
          <div>
            <h2 className="text-lg text-neut-prim font-semibold mb-4">
              {t('edit_product.sections.product_information')}
            </h2>
            <div className="p-4 bg-neut-50 rounded-lg border border-neut-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-neut-600">
                    {t('edit_product.labels.product_code')}
                  </span>
                  <span className="ml-2 font-mono font-medium text-neut-prim">
                    {
                      sellerProductInventory?.platformProductsLength
                        ?.platformProduct?.materialCode
                    }
                  </span>
                </div>
                <div>
                  <span className="text-neut-600">
                    {t('edit_product.labels.name')}
                  </span>
                  <span className="ml-2 font-medium text-neut-prim">
                    {
                      sellerProductInventory?.platformProductsLength
                        ?.platformProduct?.name
                    }
                  </span>
                </div>

                <div>
                  <span className="text-neut-600">
                    {t('edit_product.labels.species')}
                  </span>
                  <span className="ml-2 font-medium text-neut-prim">
                    {
                      sellerProductInventory?.platformProductsLength
                        ?.platformProduct?.properties?.species?.name
                    }
                  </span>
                </div>
                <div>
                  <span className="text-neut-600">
                    {t('edit_product.labels.grade')}
                  </span>
                  <span className="ml-2 font-medium text-neut-prim">
                    {
                      sellerProductInventory?.platformProductsLength
                        ?.platformProduct?.properties?.grade?.code
                    }
                  </span>
                </div>
                <div>
                  <span className="text-neut-600">
                    {t('edit_product.labels.length')}
                  </span>
                  <span className="ml-2 font-medium text-neut-prim">
                    <span className="italic text-neut-500">
                      {
                        sellerProductInventory?.platformProductsLength?.length
                          ?.displayValue
                      }
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg text-neut-prim font-semibold">
                {t('edit_product.sections.pricing_inventory')}
              </h2>
              <div>
                <Button
                  variant="secondary"
                  onClick={() => setShowSettingsModal(true)}
                >
                  <Settings size={16} />
                  {t('edit_product.buttons.stock_monitoring_settings')}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form
                formData={formData}
                formFields={getPriceAndInventoryFormFields(
                  t,
                  [{ code: inventoryUnitCode }],
                  formattedPriceWithUnit
                )}
                onSubmit={() => {}}
                includeSubmitButton={false}
              />
            </div>
          </div>

          <div>
            <h2 className="text-lg text-neut-prim font-semibold mb-4">
              {t('edit_product.sections.additional_settings')}
            </h2>
            <div className="space-y-4">
              <Form
                formData={formData}
                formFields={getAdditionalSettingsFormFields(t)}
                onSubmit={() => {}}
                includeSubmitButton={false}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const getPriceAndInventoryFormFields = (
  t: TFunction,
  units: { code: UnitCode }[],
  formattedPriceWithUnit: string
): FormField[] => [
  {
    name: 'pricePerUnit',
    label: (
      <span
        className="ml-md mb-xxs text-sm font-medium text-neut-prim"
        id="pricePerUnit"
      >
        {t('edit_product.labels.price', { unit: units[0]?.code ?? 'MBF' })}
        <span className="text-status-error"> *</span>{' '}
        <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded font-medium whitespace-nowrap">
          {t('edit_product.labels.market_price', {
            formattedPriceWithUnit: formattedPriceWithUnit,
          })}
        </span>
      </span>
    ),
    type: 'number',
    required: true,
    renderRequiredSymbol: false,
    placeholder: '0.00',
    leftIcon: <span>$</span>,
  },
  {
    name: 'unit',
    required: true,
    label: t('edit_product.labels.unit'),
    type: 'select',
    options: units.map((u) => ({ label: u.code, value: u.code })),
    placeholder: 'MBF',
  },
  {
    name: 'stock',
    label: t('edit_product.labels.stock_quantity'),
    type: 'number',
    placeholder: '0',
    required: true,
  },
  {
    name: 'minimumOrderQuantity',
    label: t('edit_product.labels.minimum_order_quantity'),
    type: 'number',
    placeholder: '1',
    required: true,
  },
  {
    name: 'readyToShipDays',
    label: t('edit_product.labels.ready_to_ship_days'),
    type: 'number',
    placeholder: '0',
    required: true,
  },
  {
    name: 'replenishmentRatePerWeek',
    label: t('edit_product.labels.replenishment_rate'),
    type: 'number',
    placeholder: '0',
    required: true,
  },
];

const getAdditionalSettingsFormFields = (t: TFunction): FormField[] => [
  {
    name: 'internalNotes',
    label: t('edit_product.labels.internal_notes'),
    type: 'textarea',
    placeholder: t('edit_product.placeholders.internal_notes'),
    fullWidth: true,
  },
  {
    name: 'status',
    label: t('edit_product.labels.product_status'),
    type: 'select',
    placeholder: 'Active',
    options: [
      {
        label: t('edit_product.options.status.visible_to_all'),
        value: 'VISIBLE_TO_BUYERS',
      },
      {
        label: t('edit_product.options.status.visible_to_my_buyers'),
        value: 'VISIBLE_TO_MY_BUYERS',
      },
      { label: t('edit_product.options.status.inactive'), value: 'INACTIVE' },
    ],
    fullWidth: true,
  },
];

const getInitialFormData = (
  inventory: SellerProductInventory | undefined | null,
  defaultUnitCode?: string
) => ({
  pricePerUnit:
    inventory?.sellerProductConfiguration.pricePerUnit.toString() ?? '',
  unit: inventory
    ? (inventory.sellerProductConfiguration.unit ?? defaultUnitCode ?? '')
    : '',
  stock: inventory?.quantity?.value.toString() ?? '',
  minimumOrderQuantity:
    inventory?.sellerProductConfiguration.minimumOrderQuantity.value.toString() ??
    '',
  readyToShipDays:
    inventory?.sellerProductConfiguration.readyToShipDays.toString() ?? '',
  replenishmentRatePerWeek:
    inventory?.sellerProductConfiguration.replenishmentRatePerWeek?.value.toString() ??
    '',
  internalNotes: inventory?.sellerProductConfiguration.internalNotes ?? '',
  status: inventory?.sellerProductConfiguration.status ?? '',
});

const formatPriceWithUnit = (latestPriceMid: number | null | undefined) => {
  return latestPriceMid ? formatCurrency(latestPriceMid) + '/MBF' : 'N/A';
};
